// @ts-check

class ContentNavigation extends HTMLElement {
	#elements;
	/** @type {MediaQueryList} Media query for reduced motion preference */
	#prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

	connectedCallback() {
		this.#elements = {
			container: this.querySelector(".ContentNavigation-scroll"),
			prevBtn: this.querySelector(".ContentNavigation-prev"),
			nextBtn: this.querySelector(".ContentNavigation-next"),
			links: this.querySelectorAll(".ContentNavigation-link"),
		};

		if (!this.#elements.container) return;

		this.#setupScrollBehavior();
		this.#setupKeyboardNavigation();

		// Use requestAnimationFrame for better paint timing
		requestAnimationFrame(() => {
			this.#updateArrowVisibility();
			this.#updateMask();
		});
	}

	#setupScrollBehavior() {
		const { container, prevBtn, nextBtn } = this.#elements;

		// Scroll on arrow button clicks
		prevBtn?.addEventListener("click", () => this.#scrollPrev());
		nextBtn?.addEventListener("click", () => this.#scrollNext());

		// Update arrows and mask on scroll
		container?.addEventListener("scroll", () => {
			this.#updateArrowVisibility();
			this.#updateMask();
		});

		// Update on resize
		window.addEventListener("resize", () => {
			this.#updateArrowVisibility();
			this.#updateMask();
		});
	}

	#setupKeyboardNavigation() {
		const { links } = this.#elements;

		links.forEach((link, index) => {
			link.addEventListener("keydown", (e) => {
				if (e.key === "ArrowLeft" && index > 0) {
					e.preventDefault();
					links[index - 1].focus();
					this.#scrollToElement(links[index - 1]);
				} else if (e.key === "ArrowRight" && index < links.length - 1) {
					e.preventDefault();
					links[index + 1].focus();
					this.#scrollToElement(links[index + 1]);
				}
			});
		});
	}

	/**
	 * Scroll to previous items
	 * Respects prefers-reduced-motion for accessibility
	 */
	#scrollPrev() {
		const { container } = this.#elements;
		if (!container) return;

		const scrollAmount = container.clientWidth * 0.8;
		container.scrollBy({
			left: -scrollAmount,
			behavior: this.#prefersReducedMotion.matches ? "auto" : "smooth",
		});
	}

	/**
	 * Scroll to next items
	 * Respects prefers-reduced-motion for accessibility
	 */
	#scrollNext() {
		const { container } = this.#elements;
		if (!container) return;

		const scrollAmount = container.clientWidth * 0.8;
		container.scrollBy({
			left: scrollAmount,
			behavior: this.#prefersReducedMotion.matches ? "auto" : "smooth",
		});
	}

	/**
	 * Scroll element into view when using keyboard navigation
	 * Respects prefers-reduced-motion for accessibility
	 * @param {Element} element - The element to scroll into view
	 */
	#scrollToElement(element) {
		const { container } = this.#elements;
		if (!container) return;

		const containerRect = container.getBoundingClientRect();
		const elementRect = element.getBoundingClientRect();

		if (
			elementRect.left < containerRect.left ||
			elementRect.right > containerRect.right
		) {
			element.scrollIntoView({
				behavior: this.#prefersReducedMotion.matches ? "auto" : "smooth",
				block: "nearest",
				inline: "center",
			});
		}
	}

	/**
	 * Update arrow button visibility based on scroll position
	 * Uses 10px tolerance for reliable edge detection across browsers
	 */
	#updateArrowVisibility() {
		const { container, prevBtn, nextBtn } = this.#elements;
		if (!container || !prevBtn || !nextBtn) return;

		const isScrollable = container.scrollWidth > container.clientWidth;
		const isAtStart = container.scrollLeft <= 10;
		const isAtEnd =
			container.scrollLeft + container.clientWidth >=
			container.scrollWidth - 10;

		// Show/hide buttons based on scroll position
		prevBtn.hidden = !isScrollable || isAtStart;
		nextBtn.hidden = !isScrollable || isAtEnd;
	}

	/**
	 * Update mask gradient based on scroll position
	 * Uses 10px tolerance for reliable edge detection across browsers
	 */
	#updateMask() {
		const { container } = this.#elements;
		if (!container) return;

		const isScrollable = container.scrollWidth > container.clientWidth;

		if (!isScrollable) {
			container.classList.remove(
				"ContentNavigation-scroll--maskLeft",
				"ContentNavigation-scroll--maskRight",
			);
			container.classList.add("ContentNavigation-scroll--maskNone");
			return;
		}

		const isAtStart = container.scrollLeft <= 10;
		const isAtEnd =
			container.scrollLeft + container.clientWidth >=
			container.scrollWidth - 10;

		container.classList.remove(
			"ContentNavigation-scroll--maskLeft",
			"ContentNavigation-scroll--maskRight",
			"ContentNavigation-scroll--maskNone",
		);

		if (isAtStart) {
			container.classList.add("ContentNavigation-scroll--maskRight");
		} else if (isAtEnd) {
			container.classList.add("ContentNavigation-scroll--maskLeft");
		}
	}
}

customElements.define("content-navigation", ContentNavigation);
